import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";

const BASIC_AUTH = "YW5sb29rQGdtYWlsLmNvbTpBbmxvb2tAMTIzNDU=";

const pick = (val, fallback) => {
  const v = val ?? "";
  const t = typeof v === "string" ? v.trim() : v;
  return t === "" ? (fallback ?? "") : v;
};

const ynOptions = [
  { value: "Y", label: "Yes (Y)" },
  { value: "N", label: "No (N)" },
];

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const EditCatalogItemModal = ({ open, item, userID, baseUrl, config, onClose, onSaved }) => {
  const original = useMemo(() => item || {}, [item]);

  const [form, setForm] = useState({
    itemName: "",
    status: "",
    itemDesc: "",
    rating: "",
    price: "",
    discount: "",
    discount_price: "",
    size: "",
    photo: "",

    // new image flow fields
    newImageData: "", // base64 "data:image/..."
    newImageFileName: "",

    item_name_trans: "",
    item_desc_trans: "",
    size_trans: "",
    icon_dish: "",
    inStock: "",
    stock: "",
    home_delivery: "",
    takeaway: "",
    dining: "",
    item_category: "",
    item_category_display: "",
    barcodeNumber: "",
  });

  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Prefill when modal opens / item changes
  useEffect(() => {
    if (!open || !item) return;

    setForm({
      itemName: original?.item_name || "",
      status: original?.status || "",
      itemDesc: original?.item_description || "",
      rating: original?.ratings || "",
      price: original?.price || "",
      discount: original?.discount || "",
      discount_price: original?.discount_price || "",
      size: original?.size || "",
      photo: original?.photo || "",

      newImageData: "",
      newImageFileName: "",

      item_name_trans: original?.item_name_trans || "",
      item_desc_trans: original?.item_description_trans || "",
      size_trans: original?.size_trans || "",
      icon_dish: original?.icon_dish || "",
      inStock: original?.inStock || "",
      stock: original?.stock || "",
      home_delivery: original?.home_delivery || "",
      takeaway: original?.takeaway || "",
      dining: original?.dining || "",
      item_category: original?.item_category || "",
      item_category_display: original?.category_name || "",
      barcodeNumber: original?.barcodeNumber || "",
    });
  }, [open, item, original]);

  const onChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handlePickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const base64 = await fileToBase64(file);
      setForm((p) => ({
        ...p,
        newImageData: base64,
        newImageFileName: file.name || "",
      }));
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to read image file", "error");
    }
  };

  // ✅ EXACT upload flow from CreateOrderModal (adapted to this modal)
  const uploadImageIfNeeded = async () => {
    // if user did not pick a new image => don't upload => return existing/or typed photo
    if (!form.newImageData) return "";

    // if for some reason newImageData is already URL (not data:)
    if (form.newImageData && !String(form.newImageData).startsWith("data:")) {
      return form.newImageData || "";
    }

    if (!form.newImageData || !String(form.newImageData).startsWith("data:")) return "";

    const requestBody = {
      userid: userID,
      item_name: form.itemName || original?.item_name || "Item",
      type: "IMG",
      image_data: form.newImageData,
    };

    const resp = await axios.post("https://bumppy.in/anlook/api/v1/uploadItemImage.php", requestBody, config);

    // you said: status === "0" and image_url
    if (resp?.data?.status === "0" && resp?.data?.image_url) {
      return resp.data.image_url;
    }
    throw new Error(resp?.data?.message || "Image upload failed");
  };

  const buildPayload = (finalPhotoUrl) => {
    return {
      id: String(original?.id || ""),
      usCode: String(userID),

      itemName: pick(form.itemName, original?.item_name),
      status: pick(form.status, original?.status),
      itemDesc: pick(form.itemDesc, original?.item_description),
      rating: pick(form.rating, original?.ratings),
      price: pick(form.price, original?.price),
      discount: pick(form.discount, original?.discount),
      discount_price: pick(form.discount_price, original?.discount_price),
      size: pick(form.size, original?.size),

      // ✅ photo comes from uploaded URL if new image selected, else existing photo (or typed)
      photo: pick(finalPhotoUrl || form.photo, original?.photo),

      item_name_trans: pick(form.item_name_trans, original?.item_name_trans),
      item_desc_trans: pick(form.item_desc_trans, original?.item_description_trans),
      size_trans: pick(form.size_trans, original?.size_trans),
      icon_dish: pick(form.icon_dish, original?.icon_dish),

      inStock: pick(form.inStock, original?.inStock),
      stock: pick(form.stock, original?.stock),
      home_delivery: pick(form.home_delivery, original?.home_delivery),
      takeaway: pick(form.takeaway, original?.takeaway),
      dining: pick(form.dining, original?.dining),
      item_category: pick(form.item_category, original?.item_category),
      barcodeNumber: pick(form.barcodeNumber, original?.barcodeNumber),
    };
  };

  const handleSave = async () => {
    if (!original?.id) {
      Swal.fire("Error", "Missing item id", "error");
      return;
    }

    const confirm = await Swal.fire({
      title: "Save changes?",
      text: "This will update the catalogue item.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Save",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#f59e0b",
    });
    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);

      // 1) upload image if user selected a new one
      let uploadedUrl = "";
      if (form.newImageData) {
        try {
          setUploadingImage(true);
          uploadedUrl = await uploadImageIfNeeded();
        } finally {
          setUploadingImage(false);
        }
      }

      // 2) call edit api with final photo url
      const payload = buildPayload(uploadedUrl);

      const headers = {
        ...(config?.headers || {}),
        Authorization: `Basic ${BASIC_AUTH}`,
        "Content-Type": "application/json",
      };

      const res = await axios.post(`${baseUrl}/api/Inventory/editCatalogItems`, payload, {
        ...(config || {}),
        headers,
      });

      if (res.data?.responseCode === "00") {
        Swal.fire("Success", res.data?.responseMessage || "Item updated", "success");
        onSaved?.();
        onClose?.();
      } else {
        Swal.fire("Failed", res.data?.responseMessage || "Failed to update item", "error");
      }
    } catch (err) {
      console.error("editCatalogItems error:", err);
      Swal.fire("Error", err?.message || "Something went wrong while saving", "error");
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  if (!open) return null;

  const currentImage = form.photo || original?.photo || "";
  const previewImage = form.newImageData || currentImage;

  return (
    <Modal show={open} onHide={onClose} centered size="lg" backdrop="static" className="inv-industrial-modal">
      <Modal.Header closeButton className="inv-industrial-modal-head">
        <Modal.Title className="inv-industrial-modal-title">
          <span className="inv-modal-title-main">Edit Item</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="inv-industrial-modal-body">
        {/* IMAGE BLOCK */}
        <div className="row g-3 mb-2">
          <div className="col-md-4">
            <label className="form-label">Current Image</label>
            <div
              style={{
                width: "100%",
                border: "1px solid rgba(0,0,0,0.1)",
                borderRadius: 10,
                overflow: "hidden",
                background: "#fff",
                minHeight: 140,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {previewImage ? (
                <img
                  src={previewImage}
                  alt="Item"
                  style={{ width: "100%", height: 180, objectFit: "cover", display: "block" }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div style={{ padding: 12, fontSize: 12, opacity: 0.7 }}>No image</div>
              )}
            </div>

            <div className="mt-2 d-flex gap-2">
              <input type="file" accept="image/*" className="form-control" onChange={handlePickImage} />
            </div>

            {form.newImageFileName ? (
              <div className="mt-2" style={{ fontSize: 12, opacity: 0.8 }}>
                Selected: <b>{form.newImageFileName}</b>
              </div>
            ) : null}

            {form.newImageData ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary mt-2"
                onClick={() => setForm((p) => ({ ...p, newImageData: "", newImageFileName: "" }))}
              >
                Remove selected image
              </button>
            ) : null}
          </div>

          <div className="col-md-8">
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">Item Name</label>
                <input className="form-control" value={form.itemName} onChange={onChange("itemName")} />
              </div>

              <div className="col-md-4">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={onChange("status")}>
                  <option value="">— Select —</option>
                  {ynOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={form.itemDesc} onChange={onChange("itemDesc")} />
              </div>

              <div className="col-md-3">
                <label className="form-label">Price</label>
                <input className="form-control" value={form.price} onChange={onChange("price")} />
              </div>

              <div className="col-md-3">
                <label className="form-label">Discount</label>
                <input className="form-control" value={form.discount} onChange={onChange("discount")} />
              </div>

              <div className="col-md-3">
                <label className="form-label">Discount Price</label>
                <input className="form-control" value={form.discount_price} onChange={onChange("discount_price")} />
              </div>

              <div className="col-md-3">
                <label className="form-label">Rating</label>
                <input className="form-control" value={form.rating} onChange={onChange("rating")} />
              </div>

              {/* keep photo url field too (optional) */}
              <div className="col-12">
                <label className="form-label">Photo URL (optional)</label>
                <input className="form-control" value={form.photo} onChange={onChange("photo")} />
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                  If you pick a new image above, it will upload and override this URL.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* REST FORM */}
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Size</label>
            <input className="form-control" value={form.size} onChange={onChange("size")} />
          </div>

          <div className="col-md-6">
            <label className="form-label">Barcode Number</label>
            <input className="form-control" value={form.barcodeNumber} onChange={onChange("barcodeNumber")} />
          </div>

          <div className="col-md-4">
            <label className="form-label">In Stock</label>
            <select className="form-select" value={form.inStock} onChange={onChange("inStock")}>
              <option value="">— Select —</option>
              {ynOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Stock</label>
            <input className="form-control" value={form.stock} onChange={onChange("stock")} />
          </div>

          <div className="col-md-4">
            <label className="form-label">Item Category</label>
            <input className="form-control" value={form.item_category} onChange={onChange("item_category")} />
          </div>

          <div className="col-md-4">
            <label className="form-label">Home Delivery</label>
            <select className="form-select" value={form.home_delivery} onChange={onChange("home_delivery")}>
              <option value="">— Select —</option>
              {ynOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Takeaway</label>
            <select className="form-select" value={form.takeaway} onChange={onChange("takeaway")}>
              <option value="">— Select —</option>
              {ynOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Dining</label>
            <select className="form-select" value={form.dining} onChange={onChange("dining")}>
              <option value="">— Select —</option>
              {ynOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Name Trans</label>
            <input className="form-control" value={form.item_name_trans} onChange={onChange("item_name_trans")} />
          </div>

          <div className="col-md-4">
            <label className="form-label">Desc Trans</label>
            <input className="form-control" value={form.item_desc_trans} onChange={onChange("item_desc_trans")} />
          </div>

          <div className="col-md-4">
            <label className="form-label">Size Trans</label>
            <input className="form-control" value={form.size_trans} onChange={onChange("size_trans")} />
          </div>

          <div className="col-12">
            <label className="form-label">Icon Dish (string / JSON string)</label>
            <input className="form-control" value={form.icon_dish} onChange={onChange("icon_dish")} />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="inv-industrial-modal-foot">
        <button className="inv-btn inv-btn-ghost" onClick={onClose} disabled={saving || uploadingImage}>
          Cancel
        </button>
        <button className="inv-btn inv-btn-warning" onClick={handleSave} disabled={saving || uploadingImage}>
          {uploadingImage ? "Uploading image..." : saving ? "Saving..." : "Save"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditCatalogItemModal;
